import { SyntheticEvent } from 'react';

import { ImageLoaderProps } from 'next/image';
import { twMerge } from 'tailwind-merge';

import { Text } from '../../Text/Text';
import { TopBaitProductUnitFields } from '../../../generated/graphql';
import { Button } from '../../Clickables/Buttons/Button';
import { NextImageWrapper } from '../../NextImage/NextImageWrapper';
import { FishCatchesIcon } from '../../Icons/FishCatchesIcon';
import { getImgProxyLoader } from '@lib/images/imgProxyLoader';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { abbreviateNumber } from '@lib/numbers/abbreviateNumber';

interface IProps {
  blur?: boolean;
  className?: string;
  productUnit: TopBaitProductUnitFields;
  numberOfCatches: number;
  position: number;
  onClick?: (event: SyntheticEvent) => void;
}

export const TopBaitsEntry = ({
  className,
  productUnit,
  numberOfCatches,
  position,
  onClick,
}: IProps) => {
  const image = productUnit.image;
  const imageUrl = image?.url ?? '';
  const imageWidth = image?.width ?? 0;
  const imageHeight = image?.height ?? 0;

  const baitName = productUnit.product.displayName;
  const baitBrand = productUnit.product.brand.name;
  const baitVariant = getNodesFromConnection(productUnit.attributeValues)[0]?.value?.name;

  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, imageWidth, imageHeight, { quality });

  return (
    <Button
      onClick={onClick}
      variant="textNone"
      className={twMerge(className, 'relative w-full py-2.5')}
    >
      <div className="relative flex items-center justify-center gap-4">
        <div className="flex items-center justify-center">
          <Text className="text-trench m-0 font-semibold" variant="sm">
            {position}
          </Text>
        </div>
        {imageUrl && imageWidth && imageHeight ? (
          <NextImageWrapper
            wrapperClassName="w-20 h-16"
            imageClassName="size-full object-contain"
            customPlaceholderIcon={
              <FishCatchesIcon className="size-full object-contain" size={93} />
            }
            customPlaceholderClassName="text-twilight p-0 bg-sea-foam sm:bg-lake-fog sm:text-twilight"
            alt={baitName}
            src={imageUrl}
            loader={imgProxyLoader}
            fill
            sizes="80px"
          />
        ) : (
          <div className="bg-twilight text-lake-fog mb-3 flex h-11 w-28 items-center justify-center rounded-md md:rounded-lg">
            <FishCatchesIcon size={93} />
          </div>
        )}
        <div className="flex grow flex-col text-left">
          <Text as="div" variant="sm" className="text-midnight m-0">
            {baitBrand}
          </Text>
          <Text as="div" variant="sm" className="text-trench m-0 font-semibold">
            {baitName}
          </Text>
          {baitVariant && (
            <Text as="div" variant="sm" className="text-midnight m-0">
              {baitVariant}
            </Text>
          )}
        </div>
        {numberOfCatches && (
          <div className="border-twilight bg-sea-foam flex min-w-14 items-center justify-center gap-1 rounded-3xl border px-3.5 py-1.5">
            <Text as="span" variant="md" className="font-semibold">
              {abbreviateNumber(numberOfCatches, true)}
            </Text>
          </div>
        )}
      </div>
    </Button>
  );
};
